import type { SupabaseClient } from '@supabase/supabase-js';

import { useSupabaseUpload } from '../hooks/use-supabase-upload';
import { cn } from '../lib/utils/cn';
import { Dropzone, DropzoneContent, DropzoneEmptyState } from './dropzone';

export const FileUploader = (props: {
  className?: string;
  maxFiles: number;
  bucketName: string;
  path?: string;
  allowedMimeTypes: string[];
  maxFileSize: number | undefined;
  cacheControl?: number;
  client: SupabaseClient;
  onUploadSuccess?: (files: string[]) => void;
  onError?: (errors: { name: string; message: string }[]) => void;
  autoUpload?: boolean;
  /**
   * When true, re-uploading a file with the same path overwrites the
   * existing object instead of returning a "Duplicate" error. Required
   * for replace flows and for tolerating retries on the same filename.
   * Defaults to false to preserve historical behavior.
   */
  upsert?: boolean;
  /**
   * HTML capture attribute for the file input.
   * Use 'user' for front-facing camera (selfies), 'environment' for rear camera (documents).
   * Only has effect on mobile devices.
   */
  capture?: 'user' | 'environment';
  /**
   * When false, the entire dropzone area is clickable (better for mobile).
   * Defaults to true (desktop drag-and-drop UX, requires clicking the
   * "select" link inside the empty state).
   */
  noClick?: boolean;
}) => {
  const { capture, className, noClick, ...uploadProps } = props;
  const uploader = useSupabaseUpload({ ...uploadProps, noClick });

  return (
    <div className={cn(className)}>
      <Dropzone {...uploader} inputProps={capture ? { capture } : undefined}>
        <DropzoneEmptyState />
        <DropzoneContent />
      </Dropzone>
    </div>
  );
};
